// Extension field types aligned with backend ExtensionFieldType enum
export enum CustomFieldType {
  INTEGER = "INTEGER",
  FLOAT = "FLOAT",
  TEXT = "TEXT",
  ENUMERATION = "ENUMERATION",
  PERCENTILE = "PERCENTILE",
  CURRENCY_USD = "CURRENCY_USD",
  DATE = "DATE",
}

export enum PrefillOption {
  COUNTRY = "country",
  STATE = "state",
  CURRENCY = "currency",
}

// Backend extension field type IDs (from ExtensionFieldType enum)
export enum ExtensionFieldTypeId {
  INTEGER = 1,
  FLOAT = 2,
  TEXT = 3,
  ENUMERATION = 4,
  PERCENTILE = 7,
  CURRENCY_USD = 8,
  DATE = 9,
}

// Mapping from frontend types to backend type IDs
export const FIELD_TYPE_TO_BACKEND_ID: Record<CustomFieldType, ExtensionFieldTypeId> = {
  [CustomFieldType.INTEGER]: ExtensionFieldTypeId.INTEGER,
  [CustomFieldType.FLOAT]: ExtensionFieldTypeId.FLOAT,
  [CustomFieldType.TEXT]: ExtensionFieldTypeId.TEXT,
  [CustomFieldType.ENUMERATION]: ExtensionFieldTypeId.ENUMERATION,
  [CustomFieldType.PERCENTILE]: ExtensionFieldTypeId.PERCENTILE,
  [CustomFieldType.CURRENCY_USD]: ExtensionFieldTypeId.CURRENCY_USD,
  [CustomFieldType.DATE]: ExtensionFieldTypeId.DATE,
};

// Mapping from backend type IDs to frontend types
export const BACKEND_ID_TO_FIELD_TYPE: Record<ExtensionFieldTypeId, CustomFieldType> = {
  [ExtensionFieldTypeId.INTEGER]: CustomFieldType.INTEGER,
  [ExtensionFieldTypeId.FLOAT]: CustomFieldType.FLOAT,
  [ExtensionFieldTypeId.TEXT]: CustomFieldType.TEXT,
  [ExtensionFieldTypeId.ENUMERATION]: CustomFieldType.ENUMERATION,
  [ExtensionFieldTypeId.PERCENTILE]: CustomFieldType.PERCENTILE,
  [ExtensionFieldTypeId.CURRENCY_USD]: CustomFieldType.CURRENCY_USD,
  [ExtensionFieldTypeId.DATE]: CustomFieldType.DATE,
};

export enum CustomFieldEntityType {
  CONTACT = "CONTACT",
  ACCOUNT = "ACCOUNT",
  ENTITY = "ENTITY",
  POSITION = "POSITION",
  OFFERING = "OFFERING",
}

export interface CustomFieldOption {
  id: string;
  label: string;
  value: string;
  isActive: boolean;
}

export interface CustomField {
  id: string;
  name: string;
  label: string;
  description?: string;
  type: CustomFieldType;
  entityType: CustomFieldEntityType;
  isActive: boolean;
  options?: CustomFieldOption[];
  defaultValue?: string;
  validationRules?: {
    minLength?: number;
    maxLength?: number;
    minValue?: number;
    maxValue?: number;
    pattern?: string;
  };
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export interface CreateCustomFieldInput {
  name: string;
  description?: string;
  type: CustomFieldType;
  entityType: CustomFieldEntityType;
  options?: Omit<CustomFieldOption, "id">[];
  defaultValue?: string;
  prefillOption?: PrefillOption;
  sectionId?: string;
  validationRules?: {
    minLength?: number;
    maxLength?: number;
    minValue?: number;
    maxValue?: number;
    pattern?: string;
  };
}

export interface CustomFieldsListResponse {
  customFields: CustomField[];
  totalCount: number;
  hasNextPage: boolean;
}

export interface CustomFieldError {
  field: string;
  message: string;
  code: string;
}

// GraphQL operation types for backend integration
export interface ExtensionFieldObjectType {
  id: string;
  name: string;
  description?: string; // Optional for now, will be available when backend schema is updated
  extensionFieldTypeId: number;
  enumName?: string;
  isMultivalued: boolean;
  enumValues: ExtensionFieldEnumObjectType[];
  activeEnumValues: ExtensionFieldEnumObjectType[];
}

export interface ExtensionFieldEnumObjectType {
  id: string;
  enumName: string;
  enumValueCode: string;
  enumValueLabel: string;
}

export interface DataVaultExtensionFieldsInput {
  includeDeleted?: boolean;
  sortBy?: string;
  sortDirection?: "ASC" | "DESC";
}

export interface CreateDataVaultExtensionFieldEnumValueInput {
  enumValueLabel: string;
}

export interface CreateDataVaultExtensionFieldInput {
  name: string;
  extensionFieldTypeId: number;
  description?: string;
  enumName?: string | null;
  enumValues?: CreateDataVaultExtensionFieldEnumValueInput[];
  isMultivalued?: boolean;
  isFilterable?: boolean;
  isSearchable?: boolean;
  sortOrder?: number;
  sectionId?: string;
}

export interface CreateDataVaultExtensionFieldPayload {
  extensionField?: ExtensionFieldObjectType;
  errors: string[];
}

export interface DeleteDataVaultExtensionFieldInput {
  fieldId: string;
}

export interface DeleteDataVaultExtensionFieldPayload {
  success: boolean;
  errors: string[];
}

export interface EditDataVaultExtensionFieldEnumOptionUpsert {
  id?: string; // Global ID for update (optional, if missing = create new)
  enumValueLabel: string;
}

export interface EditDataVaultExtensionFieldEnumOptionDelete {
  id: string; // Global ID to delete
}

export interface EditDataVaultExtensionFieldInput {
  fieldId: string; // Global ID of the extension field
  name?: string;
  enumOptionsUpsert?: EditDataVaultExtensionFieldEnumOptionUpsert[];
  enumOptionsDelete?: EditDataVaultExtensionFieldEnumOptionDelete[];
  sectionId?: string | null; // Section to assign the field to (null to remove from section)
}

export interface EditDataVaultExtensionFieldPayload {
  extensionField?: ExtensionFieldObjectType;
  errors: string[];
}

// Shared field type display options for UI components
export const FIELD_TYPE_OPTIONS = [
  { value: CustomFieldType.INTEGER, label: "Integer" },
  { value: CustomFieldType.FLOAT, label: "Float" },
  { value: CustomFieldType.TEXT, label: "Text" },
  { value: CustomFieldType.ENUMERATION, label: "Enumeration" },
  { value: CustomFieldType.PERCENTILE, label: "Percentile" },
  { value: CustomFieldType.CURRENCY_USD, label: "Currency (USD)" },
  { value: CustomFieldType.DATE, label: "Date" },
] as const;

// Helper function to get field type label for display
export const getFieldTypeLabel = (type: CustomFieldType): string => {
  const option = FIELD_TYPE_OPTIONS.find((opt) => opt.value === type);
  return option?.label || `Type ${String(type)}`;
};

// Tab type for tab name suggestions
export interface Tab {
  name: string;
}

// Section with fields type for organizing custom fields
export type SectionWithFields = {
  section: Section;
  fields: CustomField[];
};

// Tab with sections type for tab-based organization
export type TabWithSections = {
  tabName: string;
  sections: SectionWithFields[];
};

// Section types for organizing custom fields
export interface Section {
  id: string;
  name: string;
  description?: string;
  context: string;
  sortOrder: number;
  extensionFieldIds: string[]; // GraphQL Global IDs are strings
  fieldCount: number;
  tabName?: string;
  createdAt: string;
  modifiedAt?: string;
}

export interface CreateSectionInput {
  name: string;
  description?: string;
  context: string;
  tabName?: string;
  sortOrder?: number;
}

export interface UpdateSectionInput {
  id: string;
  name: string;
  description?: string;
  context: string;
  tabName?: string;
  sortOrder?: number;
}
